import { useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ampli } from '../../../../ampli';
import BlankImage from '../../../../assets/images/blank-image.png';
import { RiveAnimation } from '../../../../components/common/animations/RiveAnimation';
import { Button } from '../../../../components/common/buttons/Button';
import { Icon } from '../../../../components/common/icon/Icon';
import { newProductPageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { ProductStatusConstant } from '../../../../constants/product/product.constant';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { INewProduct } from '../../../../interfaces/NewProduct.interfaces';
import { getGeneralProductPrice, IProductForGetProductPrice } from '../../../../utils/getter/getProductPrice.util';
import { postMessageToRN } from '../../../../utils/RNCommunicator/postMessageToRN.util';
import { LoginToCheckPrice } from '../components/LoginToCheckPrice';
import { ProductStatusText } from '../components/ProductStatusText';
import { DynamicBadge } from './components/DynamicBadge';

const { PENDING, WAITING, SOLDOUT, IN_CONTRACT, ONSALE } = ProductStatusConstant;

interface NewProductDetailCardProps {
  product: INewProduct;
  section: string;
}

export const NewProductDetailCard = ({ product, section }: NewProductDetailCardProps) => {
  const navigate = useNavigate();
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const [isProductInquiryModalOpen, setIsProductInquiryModalOpen] = useState<boolean>(false);

  const isBlurredProduct = product.status === SOLDOUT || product.status === IN_CONTRACT || product.status === WAITING;

  const handleProductInquiryButtonClick = () => {
    const clickEventProperties = {
      id: product.id,
      title: product.title,
      price: product.price,
      installment_yn: product.installmentYn,
      negotiation_yn: product.negotiationYn,
      section,
    };

    ampli.pvmClickNewProductInquiry(clickEventProperties);
    ReactGA.event('pvm_click_new_product_inquiry', {
      clickEventProperties,
    });

    if (window.ReactNativeWebView) {
      postMessageToRN({ messageKey: 'link', messageParam: 'tel:0316074997' });
    } else {
      window.location.href = 'tel:0316074997';
    }
  };

  const price = useMemo<string>(() => {
    return getGeneralProductPrice({ product: product as IProductForGetProductPrice, isUserLoggedIn });
  }, [product, isUserLoggedIn]);

  return (
    <Container
      onClick={(e) => {
        e.stopPropagation();
        ampli.pvmClickNewProductDetailCard({
          id: product.id,
          title: product.title,
          price: product.price,
          installment_yn: product.installmentYn,
          negotiation_yn: product.negotiationYn,
          section,
        });
        if (isBlurredProduct) return;
        navigate(`${newProductPageInfo.detailPage.path}/${product.id}`);
      }}
    >
      <ImageWrap id={'product-list-swiper-container'}>
        {product.status === WAITING && <ProductStatusText text={'판매 예정'} />}
        {product.status === IN_CONTRACT && <ProductStatusText text={'계약 진행 중'} />}
        {product.status === SOLDOUT && <ProductStatusText text={'판매 완료'} />}

        {product.status === ONSALE ? (
          <CustomSwiper
            slidesPerView={1}
            edgeSwipeDetection
            loop
            autoplay={{ delay: 3000 }}
            pagination={product.images.filter((image) => image.type === 'LIST').length > 1}
          >
            {product.images
              .filter((image) => image.type === 'LIST')
              .map((image) => (
                <CustomSwiperSlide key={image.id}>
                  <ProductImage src={image.imageUrl} alt={product.title} onClick={() => {}} loading={'lazy'} />
                  <div className={'swiper-lazy-preloader'} />
                </CustomSwiperSlide>
              ))}
          </CustomSwiper>
        ) : (
          <Image
            src={
              product.images.filter((image) => image.type === 'LIST').length > 0
                ? product.images.filter((image) => image.type === 'LIST')[0].imageUrl
                : BlankImage
            }
            alt={product.title}
            isBlurred={product.status === SOLDOUT || product.status === IN_CONTRACT}
            loading={'lazy'}
          />
        )}

        <BadgeContainer>
          {product.badge1Visible && product.badge1Title && (
            <DynamicBadge
              title={product.badge1Title}
              textColor={product.badge1TextColor}
              backgroundColor={product.badge1BgColor}
            />
          )}
          {product.badge2Visible && product.badge2Title && (
            <DynamicBadge
              title={product.badge2Title}
              textColor={product.badge2TextColor}
              backgroundColor={product.badge2BgColor}
            />
          )}
        </BadgeContainer>
      </ImageWrap>

      {product.status !== SOLDOUT && product.installmentYn && (
        <InstallmentSection isBlurred={isBlurredProduct}>
          <InstallmentLeftSection>
            {!isBlurredProduct ? (
              <RiveAnimation name={'CircleWonAnimate'} size={'Large'} />
            ) : (
              <Icon name={'CircleWon'} size={'Large'} fill={'#FF9500'} />
            )}

            <InstallmentTitleWrapper>
              <InstallmentTitle fontWeight={700}>{'할부가 가능'}</InstallmentTitle>
              <InstallmentTitle fontWeight={500}>{'해요!'}</InstallmentTitle>
            </InstallmentTitleWrapper>
          </InstallmentLeftSection>

          <Button
            icon={{ fileName: 'AngleRight', position: 'right', fill: '#505363', size: 'Small' }}
            borderType={'round'}
            color={'filledGreyLight'}
            size={'xs'}
            text={{ size: 'sm', content: '할부 문의 하기' }}
            isDisabled={isBlurredProduct}
            onClick={() => {
              handleProductInquiryButtonClick();
              if (!isUserLoggedIn) {
                setIsProductInquiryModalOpen(true);
              }
            }}
          />
        </InstallmentSection>
      )}

      {product.status !== SOLDOUT && !product.installmentYn && (
        <InstallmentSection isBlurred={isBlurredProduct}>
          <InstallmentLeftSection>
            <Icon name={'CircleExclamation'} size={'Large'} fill={'#34C77E'} />

            <InstallmentTitleWrapper>
              <InstallmentTitle fontWeight={700}>{'전화로 쉽게 상담 '}</InstallmentTitle>
              <InstallmentTitle fontWeight={500}>{'받아보세요!'}</InstallmentTitle>
            </InstallmentTitleWrapper>
          </InstallmentLeftSection>

          <Button
            icon={{ fileName: 'AngleRight', position: 'right', fill: '#505363', size: 'Small' }}
            borderType={'round'}
            color={'filledGreyLight'}
            size={'xs'}
            text={{ size: 'sm', content: '문의하기' }}
            isDisabled={isBlurredProduct}
            onClick={() => {
              handleProductInquiryButtonClick();
              if (!isUserLoggedIn) {
                setIsProductInquiryModalOpen(true);
              }
            }}
          />
        </InstallmentSection>
      )}

      <ProductInfo isBlurred={isBlurredProduct}>
        <Title>{product.title}</Title>

        {product.meta && (
          <MetadataContainer>
            {product.meta
              .split(',')
              .map((meta) => meta.trim())
              .map((meta, index, array) => {
                return (
                  <>
                    <Metadata key={meta}>{meta}</Metadata>
                    {index !== array.length - 1 && <Metadata>{'·'}</Metadata>}
                  </>
                );
              })}
          </MetadataContainer>
        )}

        <PriceSection>
          <PriceTitleContainer>
            <PriceWrap>
              {isUserLoggedIn && product.negotiationYn && <Price>{'가격 문의'}</Price>}

              {isUserLoggedIn && !product.negotiationYn && (
                <>
                  <Price>{price}</Price>

                  {!isBlurredProduct && product.priceVisible && (
                    <DiscountWrap>
                      <Discount>{'회원가'}</Discount>
                    </DiscountWrap>
                  )}
                </>
              )}

              {!isUserLoggedIn && product.negotiationYn && (
                <LoginToCheckPrice defaultText={'가격 문의'} isLockIconShow={false} />
              )}

              {!isUserLoggedIn && !product.negotiationYn && <LoginToCheckPrice />}
            </PriceWrap>
          </PriceTitleContainer>
        </PriceSection>
      </ProductInfo>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  z-index: 1;

  gap: 16px;

  &:hover {
    cursor: pointer;
  }
`;

const BadgeContainer = styled.div`
  position: absolute;

  top: 10px;
  left: 10px;

  z-index: 50;

  display: flex;
  gap: 4px;
`;

const ImageWrap = styled.div`
  width: 100%;

  padding-top: 75%;

  position: relative;

  border-radius: 8px;

  background-color: #f2f3f5;
`;

const Image = styled.img<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  left: 0;
  top: 0;

  object-fit: cover;

  position: absolute;

  border-radius: 8px;

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};
`;

const ProductInfo = styled.div<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  gap: 6px;

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};
`;

const Title = styled.h1`
  color: var(--light-scheme-monochrome-black, #232429);
  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */

  overflow: hidden;
  text-overflow: ellipsis;

  display: block;
  white-space: nowrap;
  width: 100%;
`;

const PriceSection = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  gap: 16px;
`;

const PriceTitleContainer = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PriceWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  gap: 4px;
`;

const Price = styled.span`
  color: var(--light-scheme-monochrome-black, #232429);
  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const DiscountWrap = styled.div`
  display: flex;
  align-items: center;
`;

const Discount = styled.span`
  color: var(--light-scheme-color-primary-red, #ff3049);
  /* Display/Small/s-bold */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 15.6px */
`;

const SrollSection = styled.div``;

const CustomSwiper = styled(Swiper)`
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;
`;

const CustomSwiperSlide = styled(SwiperSlide)`
  width: 100%;
  height: 100%;

  border-radius: 8px;
  cursor: pointer;

  margin-bottom: 22px;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;

  object-fit: cover;

  border-radius: 8px;
  cursor: pointer;
`;

const TagsContainer = styled.div`
  display: flex;
  overflow: hidden;
  width: 100%;
  align-items: center;
  gap: var(--radius-medium, 8px);
`;

const Tag = styled.div`
  display: flex;
  padding: var(--radius-small, 4px) 6px;
  align-items: center;
  gap: 2px;

  border-radius: var(--radius-small, 4px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const TagText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 11.9px */
`;

const MetadataContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--radius-small, 4px);
`;

const Metadata = styled.div`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */
`;

const InstallmentSection = styled.div<{ isBlurred: boolean }>`
  display: flex;
  padding: var(--radius-medium, 8px);
  justify-content: space-between;
  align-items: center;
  align-self: stretch;

  border: 1px solid var(--Light-Scheme-Monochrome-grey20, #e4e4ed);
  border-radius: var(--radius-medium, 8px);

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};
`;

const InstallmentLeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: var(--radius-small, 4px);
`;

const InstallmentTitleWrapper = styled.div`
  display: flex;
`;

const InstallmentTitle = styled.pre<{ fontWeight: number }>`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;

  overflow: hidden;
  color: var(--Light-Scheme-Color-primary-blue, #232429);
  text-overflow: ellipsis;

  /* Display/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: ${(props) => props.fontWeight};
  line-height: 130%; /* 18.2px */
`;
